import React from 'react'
import withStyles from '@material-ui/core/styles/withStyles'
import styles from './styles'
import translator from '../../translate'
import { Box, Typography, Card, Radio, CssBaseline } from '@material-ui/core'
import { Icon } from '@iconify/react'
import { CoreButton } from 'src/components/core'
import { INTEREST } from './interests'

const RadioButton = withStyles({
  root: {
    '&$checked': {
      color: '#015FFB',
    },
  },
  checked: {},
})(props => <Radio color="default" {...props} />)

const PRODUCT_SERVICE_OPTIONS = [
  {
    label: 'B2B SaaS / Software',
    icon: 'tabler:cloud',
    iconStrokeWidth: 2,
    description:
      'You sell software to other businesses and want to drive product adoption and customer success.',
  },
  {
    label: 'B2C SaaS / Mobile App',
    icon: 'tabler:device-mobile',
    iconStrokeWidth: 2,
    description:
      'You have a consumer app or web product and want to onboard and educate your users at scale.',
  },
  {
    label: 'Physical / Hardware Product',
    icon: 'tabler:package',
    iconStrokeWidth: 2,
    description:
      'You sell a physical or hardware product and want to teach customers how to set it up and use it.',
  },
  {
    label: 'Professional Services & Consulting',
    icon: 'tabler:briefcase',
    iconStrokeWidth: 2,
    description:
      'You deliver services or consulting and want to package your expertise into repeatable training.',
  },
  {
    label: 'Other',
    icon: 'tabler:dots',
    iconStrokeWidth: 2,
    description: 'A different kind of product or service.',
  },
]

const MONETIZATION_OPTIONS = [
  {
    label: 'Selling paid courses & certifications',
    icon: 'ph:certificate',
    description:
      'Charge for courses and verified certificates through a built-in paywall.',
  },
  {
    label: 'Free lead magnet / audience growth',
    icon: 'tabler:magnet',
    iconStrokeWidth: 2,
    description:
      'Use free interactive courses to grow your audience and capture leads.',
  },
  {
    label: 'Client onboarding / coaching material',
    icon: 'tabler:users',
    iconStrokeWidth: 2,
    description:
      'Onboard clients and deliver coaching material as guided, interactive courses.',
  },
]

const TRAINING_FOCUS_OPTIONS = [
  {
    label: 'New employee onboarding',
    icon: 'tabler:user-plus',
    iconStrokeWidth: 2,
    description:
      'Get new hires productive faster with a structured first-weeks programme they can work through at their own pace.',
  },
  {
    label: 'Frontline & field team enablement',
    icon: 'tabler:building-store',
    iconStrokeWidth: 2,
    description:
      'Short, mobile-friendly training for retail, field and shift teams who are rarely at a desk.',
  },
  {
    label: 'Internal upskilling & role progression',
    icon: 'tabler:stairs-up',
    iconStrokeWidth: 2,
    description:
      'Build career paths and skill tracks that move people into their next role inside the company.',
  },
  {
    label: 'Sales & GTM team enablement',
    icon: 'tabler:target-arrow',
    iconStrokeWidth: 2,
    description:
      'Keep sales, success and partner teams current on product, positioning and competitive messaging.',
  },
  {
    label: 'Compliance & mandatory certifications',
    icon: 'tabler:shield-check',
    iconStrokeWidth: 2,
    description:
      'Deliver required training with completion tracking and certificates you can show to an auditor.',
  },
  {
    label: 'Other',
    icon: 'tabler:dots',
    iconStrokeWidth: 2,
    description: 'Something else. Tell us more on the next step.',
  },
]

const CONTENT_GOAL_OPTIONS = [
  {
    label: 'Lead generation & lead nurturing',
    icon: 'tabler:magnet',
    iconStrokeWidth: 2,
    description:
      'Use interactive courses as lead magnets, then capture and nurture the people who finish them.',
  },
  {
    label: 'Educating an open community / forum',
    icon: 'tabler:users-group',
    iconStrokeWidth: 2,
    description:
      'Give an open audience a self-serve place to learn, without gating it behind seats or logins.',
  },
  {
    label: 'Event or webinar participant enablement',
    icon: 'tabler:calendar-event',
    iconStrokeWidth: 2,
    description:
      'Prepare attendees before an event and keep the material working for you long after it ends.',
  },
  {
    label: 'Other',
    icon: 'tabler:dots',
    iconStrokeWidth: 2,
    description: 'Something else. Tell us more on the next step.',
  },
]

const DELIVERY_CONFIG = {
  [INTEREST.CUSTOMER_SUCCESS]: {
    options: PRODUCT_SERVICE_OPTIONS,
    subtitle: 'CUSTOMER SUCCESS & PRODUCT TRAINING',
    heading: 'What type of product or service do you offer?',
  },
  [INTEREST.CREATOR]: {
    options: MONETIZATION_OPTIONS,
    subtitle: 'CREATOR / SOLOPRENEUR',
    heading: 'How do you plan to deliver or monetize your content?',
  },
  [INTEREST.LD_HR]: {
    options: TRAINING_FOCUS_OPTIONS,
    subtitle: 'L&D / HR / WORKPLACE TRAINING',
    heading: 'What is your primary training focus?',
  },
  [INTEREST.MARKETING_COMMUNITY]: {
    options: CONTENT_GOAL_OPTIONS,
    subtitle: 'MARKETING & COMMUNITY',
    heading: 'What is your primary goal with your content?',
  },
}

function DeliveryMethodPage(props) {
  const { classes, interest, onContinue, onBack, onSelectDelivery, selectedDelivery } =
    props
  const config =
    DELIVERY_CONFIG[interest] || DELIVERY_CONFIG[INTEREST.CUSTOMER_SUCCESS]
  const handleSelectDelivery = label => {
    onSelectDelivery(label)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className={classes.purposePageRoot}>
      <CssBaseline />
      <Box className={classes.purposeLogo}>
        <Box
          component="img"
          src="/assets/mcg-icon.svg"
          style={{ width: 32, height: 32 }}
        />
        <Box
          component="img"
          src="/assets/mcg-text.svg"
          style={{ width: '130px', height: 32 }}
        />
      </Box>
      <div className={classes.purposeCenterContainer}>
        <div className={classes.purposeInterestWrapper}>
          <Typography
            onClick={onBack}
            className={classes.deliveryBackButton}
          >
            ‹&nbsp;&nbsp;Back
          </Typography>
          <Box mt={2} />
          <Typography className={classes.purposeSubtitle}>
            {config.subtitle}
          </Typography>
          <Box mt={2} />
          <Typography className={classes.purposeHeading}>
            {config.heading}
          </Typography>
          <Box mt={3} />
          <div className={classes.purposeOptionsContainer}>
            {config.options.map((option, index) => (
              <Card
                className={classes.deliveryOptionCard}
                style={{
                  borderColor:
                    selectedDelivery === option.label ? '#015FFB' : '#CBD0D7',
                  backgroundColor: '#ffffff',
                }}
                onClick={() => handleSelectDelivery(option.label)}
                key={'delivery_' + index}
              >
                <div className={classes.deliveryOptionInner}>
                  <div className={classes.deliveryOptionLeft}>
                    <div
                      className={classes.deliveryIconWrapper}
                      style={
                        selectedDelivery === option.label
                          ? { backgroundColor: '#E3F3FF' }
                          : undefined
                      }
                    >
                      <Icon
                        icon={option.icon}
                        width={24}
                        height={24}
                        style={{
                          color: '#015FFB',
                          ...(option.iconStrokeWidth && { strokeWidth: option.iconStrokeWidth }),
                        }}
                      />
                    </div>
                    <Typography
                      className={classes.purposeOptionText}
                      style={{
                        color:
                          selectedDelivery === option.label
                            ? '#015FFB'
                            : '#1D1D1F',
                      }}
                    >
                      {option.label}
                    </Typography>
                  </div>
                  <RadioButton
                    checked={selectedDelivery === option.label}
                    value={option.label}
                  />
                </div>
                <div
                  className={classes.deliveryDescriptionWrapper}
                  style={{
                    gridTemplateRows:
                      selectedDelivery === option.label ? '1fr' : '0fr',
                  }}
                >
                  <div style={{ overflow: 'hidden' }}>
                    <Typography className={classes.deliveryOptionDescription}>
                      {option.description}
                    </Typography>
                  </div>
                </div>
              </Card>
            ))}
          </div>
          <Box mt={3} />
          <CoreButton
            onClick={selectedDelivery ? onContinue : undefined}
            variant="contained"
            size="large"
            fullWidth
            style={{
              backgroundColor: selectedDelivery ? undefined : '#919EAB',
              borderRadius: '10px',
              height: '40px',
              color: '#fff',
              cursor: selectedDelivery ? 'pointer' : 'default',
            }}
          >
            {translator._('signUp.continue')}
          </CoreButton>
          <Typography
            onClick={() => {
              onSelectDelivery('')
              onContinue()
            }}
            className={classes.purposeSkipButton}
          >
            Skip
          </Typography>
        </div>
      </div>
    </div>
  )
}

export default withStyles(styles)(DeliveryMethodPage)
